/**
 * tree-scatter.ts — produce tree/prop placements for a terrain.
 *
 * Source of truth for *where* trees may stand:
 *   - painted annotations are authoritative at that pixel;
 *   - transparent annotation pixels fall back to the terrain surface;
 *   - the allowed surface set is terrain-configurable and defaults to forest.
 *
 * Pixel→world mapping matches `building-extrude.ts` so trees, buildings,
 * docks and roads share one canonical coordinate system.
 *
 * Vetoes (in order, cheap → expensive):
 *   1. Centre pixel must match an allowed raster surface.
 *   2. No annotation water and no carved-below-water terrain mesh
 *      within a 5 m canopy ring around the trunk.
 *   3. Ground height >= -0.05 m so the trunk sits on essentially dry mesh
 *      (small tolerance so flat near-water banks still grow trees).
 *   4. Optional caller `minGroundM` and shoreline ring guards.
 *   5. Reject cliff edges (>2 m drop within 2 m).
 */

import type { TerrainAsset, TerrainSurface } from "../assets/TerrainAsset";
import {
  sampleTerrainMarker,
  sampleTerrainHeight,
  sampleTerrainSurface,
  terrainClassificationDimensions,
} from "../assets/TerrainAsset";

export interface TreePlacement {
  x: number;
  z: number;
  /** Ground height at (x,z) in metres (heightmap sample, ≥ 0 above water). */
  y: number;
  /** Absolute height scale (≈ 4–11 m for default settings). */
  height: number;
  /** Crown radius (≈ 1–2.5 m). */
  radius: number;
  yaw: number;
}

export interface TreeScatterOptions {
  /** Target spacing in metres between trees (one tree per cellM × cellM cell). */
  cellM?: number;
  /** Probability that a forest cell actually emits a tree (thins density). */
  density?: number;
  /** Minimum / maximum tree height in metres. */
  heightMin?: number;
  heightMax?: number;
  /** Maximum number of trees to emit (safety cap). */
  maxCount?: number;
  /** RNG seed for reproducible placement. */
  seed?: number;
  /** Optional extra freeboard guard (metres above water level) at (x, z). */
  minGroundM?: number;
  /** When > 0, also reject placements where any of 8 samples on a ring of
   *  this radius is below water level. Eliminates the "tree on a tiny
   *  islet poking out of a bay" artefact. */
  shoreRingM?: number;
  /** Optional downward bias applied to the returned `y` (metres). Used
   *  by tall layers (trees) to swallow per-vertex interpolation slop on
   *  slopes; small props should leave it at 0 so they don't sink. */
  sinkM?: number;
  /** When > 0, only accept placements that have water within this radius
   *  (sampled on an 8-point ring against annotations + terrain height). Use it
   *  for shore-loving layers (flowers, grass) so they cluster near
   *  banks instead of dotting the deep forest interior. */
  nearShoreM?: number;
  /** Surface classes allowed to emit trees. Defaults to forest only. */
  allowedSurfaces?: readonly TerrainSurface[];
}

/** Trunk-centre water/cliff veto. Returns true when the canopy clearly
 *  overhangs water — but tolerates a small fraction of the ring being
 *  water/dock/underwater so that trees on a normal canal or lake bank
 *  (where some of the 5 m ring inevitably crosses the water edge) are
 *  still allowed. The trunk-pixel surface check happens earlier; here
 *  we only veto when the canopy is *mostly* over water. */
function canopyOverWater(terrain: TerrainAsset, x: number, z: number): boolean {
  const RING_M = 5.0;
  const SAMPLES = 16;
  const UNDERWATER_TOL_M = 0.4;
  const MAX_WATER_SAMPLES = 4; // ≈ 25% of the ring may hang over water.
  let waterSamples = 0;
  for (let i = 0; i < SAMPLES; i++) {
    const ang = (i / SAMPLES) * Math.PI * 2;
    const sx = x + Math.cos(ang) * RING_M;
    const sz = z + Math.sin(ang) * RING_M;
    const surface = sampleTerrainSurface(terrain, sx, sz);
    const isWater =
      surface === "water" ||
      surface === "dock" ||
      sampleTerrainHeight(terrain, sx, sz) < -UNDERWATER_TOL_M;
    if (isWater) {
      waterSamples++;
      if (waterSamples > MAX_WATER_SAMPLES) return true;
    }
  }
  return false;
}

export function scatterTreesFromTerrainAnnotations(
  terrain: TerrainAsset,
  opts: TreeScatterOptions = {},
): TreePlacement[] {
  const dimensions = terrainClassificationDimensions(terrain);
  if (!dimensions) return [];
  const cellM = opts.cellM ?? 12;
  const density = opts.density ?? 0.5;
  const heightMin = opts.heightMin ?? 5;
  const heightMax = opts.heightMax ?? 11;
  const maxCount = opts.maxCount ?? 4000;
  const seed = opts.seed ?? 1337;
  const shoreRingM = opts.shoreRingM ?? 0;
  const userMinGround = opts.minGroundM ?? 0;
  const sinkM = opts.sinkM ?? 0;
  const nearShoreM = opts.nearShoreM ?? 0;
  const allowedSurfaces = new Set<TerrainSurface>(
    opts.allowedSurfaces?.length ? opts.allowedSurfaces : ["forest"],
  );

  const wM = terrain.worldWidthM;
  const hM = terrain.worldHeightM;
  const halfW = wM * 0.5;
  const halfH = hM * 0.5;

  // Iterate the annotation raster directly.
  // Using the same pixel→world formula as building-extrude.ts keeps
  // forests aligned with buildings, docks and roads.
  const aw = dimensions.width;
  const ah = dimensions.height;
  const pxToWorldX = wM / aw;
  const pxToWorldZ = hM / ah;
  const strideX = Math.max(1, Math.round(cellM / pxToWorldX));
  const strideZ = Math.max(1, Math.round(cellM / pxToWorldZ));

  // Cheap deterministic RNG.
  let s = seed >>> 0;
  const rand = () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const out: TreePlacement[] = [];
  for (let py = 0; py < ah && out.length < maxCount; py += strideZ) {
    for (let px = 0; px < aw && out.length < maxCount; px += strideX) {
      if (rand() > density) continue;
      // Jitter inside the cell (in pixels) so trees aren't on a perfect grid.
      const jpx = Math.min(aw - 1, px + Math.floor(rand() * strideX));
      const jpy = Math.min(ah - 1, py + Math.floor(rand() * strideZ));
      const x = -halfW + (jpx + 0.5) * pxToWorldX;
      const z = -halfH + (jpy + 0.5) * pxToWorldZ;

      // 1. Allowed surface classification at the trunk pixel.
      if (sampleTerrainMarker(terrain, x, z)) continue;
      const surface = sampleTerrainSurface(terrain, x, z);
      if (!allowedSurfaces.has(surface)) continue;

      // 2. Canopy clearance (water + carved-mesh ring around trunk).
      if (canopyOverWater(terrain, x, z)) continue;

      // 3. Trunk must sit on (or essentially on) dry mesh. A small
      //    negative tolerance lets trees grow on flat marshy banks that
      //    sit within a few centimetres of the water plane — common
      //    along canals and lowland shores where forest naturally meets
      //    water.
      const ground = sampleTerrainHeight(terrain, x, z);
      if (ground < -0.05) continue;
      if (userMinGround > 0 && ground < userMinGround) continue;

      // 4. Optional shoreline ring check (caller-tunable).
      if (shoreRingM > 0) {
        let anyUnderwater = false;
        for (let i = 0; i < 8; i++) {
          const ang = (i / 8) * Math.PI * 2;
          const gx = sampleTerrainHeight(
            terrain,
            x + Math.cos(ang) * shoreRingM,
            z + Math.sin(ang) * shoreRingM,
          );
          if (gx < 0.0) {
            anyUnderwater = true;
            break;
          }
        }
        if (anyUnderwater) continue;
      }

      // 5. Cliff-edge guard.
      let minRing = ground;
      for (let i = 0; i < 8; i++) {
        const ang = (i / 8) * Math.PI * 2;
        const gx = sampleTerrainHeight(
          terrain,
          x + Math.cos(ang) * 2.0,
          z + Math.sin(ang) * 2.0,
        );
        if (gx < minRing) minRing = gx;
      }
      if (ground - minRing > 2.0) continue;

      // 6. Optional shore-loving filter: at least one of 8 samples on a
      //    ring of `nearShoreM` must be water or carved below the water plane.
      //    Used by flowers / grass so they cluster along banks.
      if (nearShoreM > 0) {
        let nearWater = false;
        for (let i = 0; i < 8; i++) {
          const ang = (i / 8) * Math.PI * 2;
          const sx = x + Math.cos(ang) * nearShoreM;
          const sz = z + Math.sin(ang) * nearShoreM;
          if (sampleTerrainSurface(terrain, sx, sz) === "water") {
            nearWater = true;
            break;
          }
          if (sampleTerrainHeight(terrain, sx, sz) < 0) {
            nearWater = true;
            break;
          }
        }
        if (!nearWater) continue;
      }

      // Optional sink to swallow per-vertex interpolation slop on slopes
      // (caller-tunable; default 0 so small props don't sink).
      const h = heightMin + rand() * (heightMax - heightMin);
      out.push({
        x,
        z,
        y: ground - sinkM,
        height: h,
        radius: h * (0.28 + rand() * 0.12),
        yaw: rand() * Math.PI * 2,
      });
    }
  }
  return out;
}
